import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { money } from "@owners/format";
import { api } from "../api";
import { ReportView, Saved, type Model } from "./MyReports";

/**
 * Receiver Reports (SPEC §13) — the Special Servicer's My Reports.
 * Landing (tiles + one card per receivership) → Build (order details, sections,
 * Lender / Court filing, month, the receiver's monthly inputs with Request,
 * narrative with Redraft, live preview) → My Saved Reports.
 * Download is never blocked: outstanding inputs print in a red box.
 */

export interface Order {
  id: string; hotelId: string; hotelName?: string; appointedOn: string; closedOn: string | null; status: "active" | "closed";
  court: string; caseNumber: string; caseCaption: string | null; receiverName: string; receiverFirm: string | null;
  lender: string; specialServicer: string | null; dueDay: number; receiverFeeMonthly: number | null; receiverFeeTerms: string | null;
  managerFeePct: number; bankAccounts: Array<{ key: string; label: string; bank?: string | null; last4?: string | null }>;
}
export interface InputState {
  kind: string; accountKey: string; label: string; required: boolean; received: boolean; requestedAt: string | null; advancesInMonth?: number;
  input: null | { id: string; note: string | null; fileName: string | null; submittedAt: string; submittedBy: string | null };
}
export interface Detail {
  receivership: Order; months: Array<{ period: string; label: string; partial: boolean }>; period: string | null; due: string | null;
  inputs: InputState[]; advances: Array<{ id: string; advanced_on: string; amount: number; purpose: string; reference: string | null }>;
}
interface Card extends Order {
  month: { period: string; label: string } | null; due?: string; pkg: { kind: string; text: string }; outstanding: number;
  noi: number | null; advancesSinceAppointment: number; months: Array<{ period: string; label: string }>;
}
interface Landing {
  tiles: { receiverships: number; packagesMonth: string; packagesReceived: number; packagesExpected: number; inputsOutstanding: number; advancesSinceAppointment: number };
  receiverships: Card[];
}

export const fmtDate = (d: string | null | undefined) => d ? new Date(d.length === 10 ? d + "T12:00:00Z" : d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: d.length === 10 ? "UTC" : undefined }) : "—";
export const statusPill = (k: string) => (k === "received" ? "ok-pill" : k === "late" ? "bad-pill" : "warn-pill");

export function ReceiverReports() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as "home" | "build" | "saved") ?? (params.get("id") ? "build" : "home");
  const [savedId, setSavedId] = useState<string | null>(null);
  const go = (t: string, extra: Record<string, string> = {}) => setParams({ tab: t, ...extra });
  return (
    <>
      <div className="eyebrow">Owners Module · Receiver Reports</div>
      <h1>Receiver Reports</h1>
      <div className="row" style={{ marginBottom: 16 }}>
        <button className={`btn ${tab === "home" ? "" : "secondary"}`} onClick={() => go("home")}>Receiverships</button>
        <button className={`btn ${tab === "build" ? "" : "secondary"}`} onClick={() => go("build", params.get("id") ? { id: params.get("id")! } : {})}>Build report</button>
        <button className={`btn ${tab === "saved" ? "" : "secondary"}`} onClick={() => go("saved")}>My Saved Reports</button>
      </div>
      {tab === "home" && <Home onBuild={(id, period) => go("build", { id, ...(period ? { period } : {}) })} />}
      {tab === "build" && <Builder key={params.get("id") ?? "new"} initialId={params.get("id")} initialPeriod={params.get("period")} onSaved={(id) => { setSavedId(id); go("saved"); }} />}
      {tab === "saved" && <Saved base="/api/receivers/reports" selected={savedId} onSelect={setSavedId} empty="Receiver reports you download are kept here." />}
    </>
  );
}

function Home({ onBuild }: { onBuild: (id: string, period?: string) => void }) {
  const q = useQuery({ queryKey: ["receivers"], queryFn: () => api<Landing>("/api/receivers") });
  if (q.isLoading) return <p className="muted">Loading…</p>;
  if (q.error) return <p className="error">{(q.error as Error).message}</p>;
  const d = q.data!;
  return (
    <>
      <div className="tiles">
        <div className="card tile"><div className="eyebrow">Receiverships</div><div className="tile-value">{d.tiles.receiverships}</div></div>
        <div className="card tile"><div className="eyebrow">{d.tiles.packagesMonth} packages</div><div className="tile-value">{d.tiles.packagesReceived} of {d.tiles.packagesExpected}</div><div className="tile-sub">inputs complete</div></div>
        <div className="card tile"><div className="eyebrow">Outstanding inputs</div><div className="tile-value" style={{ color: d.tiles.inputsOutstanding ? "var(--bad)" : undefined }}>{d.tiles.inputsOutstanding}</div></div>
        <div className="card tile"><div className="eyebrow">Lender advances since appointment</div><div className="tile-value">{money(d.tiles.advancesSinceAppointment)}</div></div>
      </div>
      {d.receiverships.length === 0 ? (
        <div className="card"><p className="muted" style={{ margin: 0 }}>No receiverships on your hotels yet. The receiver enters the order details on their Receivership page.</p></div>
      ) : (
        <div className="tiles" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}>
          {d.receiverships.map((c) => (
            <div key={c.id} className="card" style={{ marginBottom: 0 }}>
              <div className="row" style={{ justifyContent: "space-between" }}>
                <strong>{c.hotelName}</strong>
                <span className={`pill ${c.status === "closed" ? "" : statusPill(c.pkg.kind)}`}>{c.status === "closed" ? "Closed" : c.pkg.text}</span>
              </div>
              <div className="muted" style={{ fontSize: 12, margin: "4px 0 10px" }}>{c.court} · Case {c.caseNumber}{c.closedOn ? ` · closed ${fmtDate(c.closedOn)}` : ""}</div>
              <table><tbody>
                <tr><td className="muted">Month</td><td className="num">{c.month?.label ?? "—"}</td></tr>
                <tr><td className="muted">NOI</td><td className="num">{c.noi === null ? <span className="tbd">TBD</span> : money(c.noi)}</td></tr>
                <tr><td className="muted">Advances since appointment</td><td className="num">{money(c.advancesSinceAppointment)}</td></tr>
                <tr><td className="muted">Due</td><td className="num">day {c.dueDay}{c.due ? ` · ${fmtDate(c.due)}` : ""}</td></tr>
                <tr><td className="muted">Inputs outstanding</td><td className="num" style={{ color: c.outstanding ? "var(--bad)" : undefined }}>{c.outstanding}</td></tr>
              </tbody></table>
              <button className="btn gold" style={{ marginTop: 10, width: "100%" }} onClick={() => onBuild(c.id, c.month?.period)}>Build report</button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

const SECTIONS = [
  ["financials", "Financials"], ["performance", "Performance"], ["property_condition", "Property condition"], ["compliance", "Compliance"],
  ["staffing", "Staffing"], ["narrative", "Narrative"], ["fees_advances", "Fees & advances"],
] as const;

function Builder({ initialId, initialPeriod, onSaved }: { initialId: string | null; initialPeriod: string | null; onSaved: (id: string) => void }) {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["receivers"], queryFn: () => api<Landing>("/api/receivers") });
  const [rid, setRid] = useState(initialId ?? "");
  const [period, setPeriod] = useState(initialPeriod ?? "");
  const [version, setVersion] = useState<"lender" | "court">("lender");
  const [sections, setSections] = useState<string[]>(SECTIONS.map(([k]) => k));
  const [format, setFormat] = useState<"pdf" | "xlsx" | "both">("pdf");
  const [narrative, setNarrative] = useState<string | null>(null);   // null = auto-draft (Redraft)

  useEffect(() => { if (!rid && list.data?.receiverships[0]) setRid(list.data.receiverships[0].id); }, [list.data, rid]);
  const detail = useQuery({ queryKey: ["receiver", rid, period], enabled: !!rid,
    queryFn: () => api<Detail>(`/api/receivers/${rid}${period ? `?period=${period}` : ""}`) });
  useEffect(() => { if (detail.data?.period && !period) setPeriod(detail.data.period); }, [detail.data, period]);

  const settings = useMemo(() => ({
    receivershipId: rid, period, version, sections, format,
    narrative: narrative === null ? null : narrative.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean),
  }), [rid, period, version, sections, format, narrative]);
  const ready = !!rid && !!period && sections.length > 0;
  const [debounced, setDebounced] = useState(settings);
  useEffect(() => { const h = setTimeout(() => setDebounced(settings), 500); return () => clearTimeout(h); }, [settings]);
  const preview = useQuery({ queryKey: ["receiver-preview", debounced], enabled: ready && !!debounced.period,
    queryFn: () => api<Model>("/api/receivers/reports/preview", { method: "POST", body: debounced }), staleTime: 60_000 });

  const request = useMutation({
    mutationFn: (i: InputState) => api(`/api/receivers/${rid}/requests`, { method: "POST", body: { period, kind: i.kind, accountKey: i.accountKey } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["receiver", rid] }); qc.invalidateQueries({ queryKey: ["receiver-preview"] }); },
  });
  const build = useMutation({
    mutationFn: () => api<{ id: string; pdf: boolean; xlsx: boolean }>("/api/receivers/reports/build", { method: "POST", body: settings }),
    onSuccess: (r) => {
      if (r.pdf) window.location.assign(`/api/receivers/reports/saved/${r.id}/file?format=pdf`);
      if (r.xlsx) setTimeout(() => window.location.assign(`/api/receivers/reports/saved/${r.id}/file?format=xlsx`), r.pdf ? 900 : 0);
      qc.invalidateQueries({ queryKey: ["reports-saved"] });
      setTimeout(() => onSaved(r.id), 1500);
    },
  });

  const d = detail.data;
  const o = d?.receivership;
  const autoNarrative = (preview.data?.scopes[0]?.blocks.find((b) => b.key === "narrative") as any)?.paragraphs as string[] | undefined;
  const toggle = (k: string) => setSections(sections.includes(k) ? sections.filter((x) => x !== k) : [...sections, k]);

  if (list.data && list.data.receiverships.length === 0) return <div className="card"><p className="muted" style={{ margin: 0 }}>No receiverships to report on yet.</p></div>;

  return (
    <div className="row" style={{ alignItems: "flex-start", gap: 18, flexWrap: "nowrap" }}>
      <div className="card" style={{ width: 400, flex: "none" }}>
        <Step n={1} title="Receivership">
          <select style={{ width: "100%" }} value={rid} onChange={(e) => { setRid(e.target.value); setPeriod(""); setNarrative(null); }}>
            {(list.data?.receiverships ?? []).map((c) => <option key={c.id} value={c.id}>{c.hotelName} · {c.caseNumber}</option>)}
          </select>
        </Step>
        {o && (
          <Step n={2} title="Order details">
            <div className="muted" style={{ fontSize: 12, lineHeight: 1.6 }}>
              Appointed {fmtDate(o.appointedOn)} · {o.court} · Case {o.caseNumber}<br />
              Receiver: {o.receiverName}{o.receiverFirm ? `, ${o.receiverFirm}` : ""}<br />
              Lender: {o.lender}{o.specialServicer ? ` · Servicer: ${o.specialServicer}` : ""}<br />
              Due day {o.dueDay} · Receiver fee {o.receiverFeeMonthly == null ? <span className="tbd">not set</span> : `${money(o.receiverFeeMonthly)}/mo`} · Manager fee {(o.managerFeePct * 100).toFixed(1)}%<br />
              Accounts: {o.bankAccounts.length ? o.bankAccounts.map((b) => `${b.label}${b.last4 ? ` ··${b.last4}` : ""}`).join(", ") : "none entered"}
            </div>
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>Set by the receiver.</div>
          </Step>
        )}
        <Step n={3} title="Sections required by the order">
          {SECTIONS.map(([k, l]) => <label key={k} style={{ display: "flex", gap: 6, fontSize: 13 }}><input type="checkbox" checked={sections.includes(k)} onChange={() => toggle(k)} /> {l}</label>)}
        </Step>
        <Step n={4} title="Version">
          <div className="seg">
            <button className={version === "lender" ? "on" : ""} onClick={() => setVersion("lender")}>Lender</button>
            <button className={version === "court" ? "on" : ""} onClick={() => setVersion("court")}>Court filing</button>
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{version === "court" ? "Adds the case caption and the receiver's verification page (signature and date TBD)." : "Includes every lender advance since appointment."}</div>
        </Step>
        <Step n={5} title="Month">
          <select value={period} onChange={(e) => { setPeriod(e.target.value); setNarrative(null); }} disabled={!d?.months.length}>
            {(d?.months ?? []).map((m) => <option key={m.period} value={m.period}>{m.label}</option>)}
          </select>
          {d && !d.months.length && <span className="muted" style={{ fontSize: 12 }}> The first report is due after the appointment month ends.</span>}
          {d?.due && <span className="muted" style={{ fontSize: 12 }}> · due {fmtDate(d.due)}</span>}
        </Step>
        {d && d.period && (
          <Step n={6} title="Monthly inputs from the receiver">
            <table style={{ fontSize: 12 }}><tbody>
              {d.inputs.map((i) => (
                <tr key={i.kind + i.accountKey}>
                  <td style={{ padding: "5px 4px" }}>{i.label}{!i.required && <span className="muted"> · optional</span>}
                    {i.input?.fileName && <div><a href={`/api/receivership-inputs/${i.input.id}/file`}>{i.input.fileName}</a></div>}
                    {i.input?.note && <div className="muted">{i.input.note}</div>}
                    {i.kind === "advances" && !!i.advancesInMonth && <div className="muted">{i.advancesInMonth} logged this month</div>}
                  </td>
                  <td style={{ padding: "5px 4px", textAlign: "right", whiteSpace: "nowrap" }}>
                    {i.received ? <span className="pill ok-pill">Received</span>
                      : !i.required ? <span className="muted">—</span>
                      : i.requestedAt ? <span className="pill warn-pill" title={`Requested ${fmtDate(i.requestedAt)}`}>Requested</span>
                      : <button className="btn secondary" style={{ padding: "2px 8px", fontSize: 12 }} disabled={request.isPending} onClick={() => request.mutate(i)}>Request</button>}
                  </td>
                </tr>
              ))}
            </tbody></table>
            {request.error && <p className="error">{(request.error as Error).message}</p>}
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>Download is always allowed; anything outstanding prints in a red box at the top.</div>
          </Step>
        )}
        {sections.includes("narrative") && (
          <Step n={7} title="Narrative">
            {narrative === null ? (
              <button className="btn secondary" style={{ fontSize: 12 }} disabled={!autoNarrative} onClick={() => setNarrative((autoNarrative ?? []).join("\n\n"))}>Edit the drafted narrative</button>
            ) : (
              <>
                <textarea style={{ width: "100%", minHeight: 160, fontSize: 13 }} value={narrative} onChange={(e) => setNarrative(e.target.value)} />
                <button className="btn link" style={{ fontSize: 12 }} onClick={() => setNarrative(null)}>Redraft</button>
              </>
            )}
          </Step>
        )}
        <Step n={8} title="File format">
          <div className="seg">
            {([["pdf", "PDF"], ["xlsx", "Excel"], ["both", "PDF + Excel"]] as const).map(([k, l]) => <button key={k} className={format === k ? "on" : ""} onClick={() => setFormat(k)}>{l}</button>)}
          </div>
        </Step>
        {build.error && <p className="error">{(build.error as Error).message}</p>}
        <button className="btn gold" style={{ width: "100%", marginTop: 8 }} disabled={!ready || build.isPending} onClick={() => build.mutate()}>
          {build.isPending ? "Building…" : "Download"}
        </button>
      </div>
      <div className="card report-preview" style={{ flex: 1, minWidth: 0, maxHeight: "80vh", overflowY: "auto" }}>
        <div className="eyebrow" style={{ marginBottom: 8 }}>Live preview</div>
        {!ready ? <p className="muted">Pick a receivership, a month and at least one section.</p>
          : preview.isLoading ? <p className="muted">Building preview…</p>
          : preview.error ? <p className="error">{(preview.error as Error).message}</p>
          : preview.data && <ReportView m={preview.data} />}
      </div>
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}><span className="muted">{n}.</span> {title}</div>
      {children}
    </div>
  );
}
